import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";
import { useAuthStore, AuthUser } from "@/stores/authStore";

interface LoginPayload {
  email: string;
  password: string;
  organizationSlug?: string;
}

/**
 * `POST /auth/login` puede devolver dos formas distintas (ver
 * `auth.service.ts` en el backend, `LoginResult`): sesión completa, o un
 * resultado intermedio que exige el segundo paso de 2FA. `useLogin` NO
 * abre sesión automáticamente en el segundo caso — es la pantalla de
 * login (`LoginPage`) la que decide mostrar el paso de verificación.
 */
type LoginResponseData = { requiresTwoFactor: false; user: AuthUser; accessToken: string } | { requiresTwoFactor: true; mfaToken: string };

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: LoginPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<LoginResponseData>>("/auth/login", payload);
      return data.data;
    },
    onSuccess: (data) => {
      if (data.requiresTwoFactor) return;
      setSession(data.user, data.accessToken);
      queryClient.invalidateQueries();
    },
  });
}

export function useVerifyTwoFactorLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { mfaToken: string; code: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<{ requiresTwoFactor: false; user: AuthUser; accessToken: string }>>(
        "/auth/login/verify-2fa",
        payload
      );
      return data.data;
    },
    onSuccess: (data) => {
      setSession(data.user, data.accessToken);
      queryClient.invalidateQueries();
    },
  });
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.post("/auth/logout");
    },
    onSettled: () => {
      logout();
      queryClient.clear();
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (payload: { email: string; organizationSlug?: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<null>>("/auth/forgot-password", payload);
      return data;
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (payload: { token: string; newPassword: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<null>>("/auth/reset-password", payload);
      return data;
    },
  });
}

export function useCurrentUser(enabled: boolean) {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<AuthUser>>("/auth/me");
      return data.data;
    },
    enabled,
    retry: false,
  });
}

export function useActiveSessions() {
  return useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiSuccessResponse<Array<{ id: string; current: boolean; ipAddress: string | null; userAgent: string | null; createdAt: string; expiresAt: string }>>
      >("/auth/sessions");
      return data.data;
    },
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await apiClient.delete(`/auth/sessions/${sessionId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] }),
  });
}

/**
 * Autenticación en dos pasos (2FA/TOTP) — trabajo de continuación posterior
 * a las 26 fases originales (ver docs/PROGRESO.md, cierre de la Fase 26).
 */
export interface TwoFactorStatus {
  enabled: boolean;
  recoveryCodesRemaining: number;
}

export function useTwoFactorStatus() {
  return useQuery({
    queryKey: ["auth", "2fa", "status"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<TwoFactorStatus>>("/auth/2fa/status");
      return data.data;
    },
  });
}

export interface TwoFactorSetup {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
}

export function useSetupTwoFactor() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<TwoFactorSetup>>("/auth/2fa/setup");
      return data.data;
    },
  });
}

export function useConfirmTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<{ recoveryCodes: string[] }>>("/auth/2fa/confirm", { code });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth", "2fa", "status"] }),
  });
}

export function useDisableTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (currentPassword: string) => {
      await apiClient.post("/auth/2fa/disable", { currentPassword });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth", "2fa", "status"] }),
  });
}
